import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import type { CaseIntervention } from "@/mocks/caseManagement";
import { interventionService } from "@/services/api";
import { mapDbInterventionToFrontend } from "@/services/mapping";

export default function SeguimientosPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [tipoFilter, setTipoFilter] = useState("todos"); // todos, psicologico, orientacion
  const [seguimientos, setSeguimientos] = useState<CaseIntervention[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        setError("");
        const data = await interventionService.list();
        if (Array.isArray(data)) {
          const mapped = data
            .map(mapDbInterventionToFrontend)
            .filter((i) => i.tipoIntervencion.startsWith("seguimiento"));
          setSeguimientos(mapped);
        }
      } catch (err: any) {
        console.error("Error loading followups:", err);
        setError("Error al cargar seguimientos desde Azure: " + (err.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const filtered = useMemo(() => {
    return seguimientos.filter((item) => {
      // Filter by type
      if (tipoFilter !== "todos" && !item.tipoIntervencion.includes(tipoFilter)) {
        return false;
      }
      // Search term
      if (search.trim()) {
        const q = search.toLowerCase();
        return (
          item.descripcion.toLowerCase().includes(q) ||
          item.acuerdos.toLowerCase().includes(q) ||
          item.responsableName.toLowerCase().includes(q) ||
          (item.proximosPasos && item.proximosPasos.toLowerCase().includes(q))
        );
      }
      return true;
    });
  }, [seguimientos, search, tipoFilter]);

  const getTipoLabel = (tipo: string) => {
    switch (tipo) {
      case "seguimiento_psicologico":
        return "Seguimiento Psicología";
      case "seguimiento_orientacion":
        return "Seguimiento Orientación";
      default:
        return "Seguimiento";
    }
  };

  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case "seguimiento_psicologico":
        return "bg-teal-50 text-teal-700 border-teal-100";
      case "seguimiento_orientacion":
        return "bg-purple-50 text-purple-700 border-purple-100";
      default:
        return "bg-background-50 text-foreground-700 border-background-150";
    }
  };

  const getTipoIcon = (tipo: string) => {
    switch (tipo) {
      case "seguimiento_psicologico":
        return "ri-brain-line";
      case "seguimiento_orientacion":
        return "ri-compass-line";
      default:
        return "ri-line-chart-line";
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6 animate-fade-in-up">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 text-xs px-4 py-2.5 rounded-xl flex items-center gap-2">
          <i className="ri-error-warning-line text-sm flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground-950 tracking-tight">Seguimientos y Compromisos</h1>
          <p className="text-sm text-foreground-500 mt-1">Bitácora de seguimiento escolar y compromisos pactados con estudiantes y apoderados</p>
        </div>
        <button
          onClick={() => navigate("/intervenciones/nueva")}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-white rounded-xl text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer shadow-soft whitespace-nowrap"
        >
          <i className="ri-add-line" />
          Registrar Seguimiento
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400">
            <i className="ri-search-line text-sm" />
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar en descripción, acuerdos, responsable..."
            className="w-full pl-9 pr-3 py-2 bg-background-50 border border-background-200 rounded-xl text-sm outline-none focus:border-primary-300 transition-all"
          />
        </div>
        <select
          value={tipoFilter}
          onChange={(e) => setTipoFilter(e.target.value)}
          className="px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-700 outline-none cursor-pointer"
        >
          <option value="todos">Todos los seguimientos</option>
          <option value="psicologico">Seguimiento Psicológico</option>
          <option value="orientacion">Seguimiento de Orientación</option>
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line text-2xl text-foreground-300 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-background-200 rounded-2xl p-12 text-center">
          <i className="ri-line-chart-line text-5xl text-foreground-200 mb-4 block" />
          <p className="text-base font-medium text-foreground-600">No se encontraron seguimientos</p>
          <p className="text-sm text-foreground-400 mt-1">Prueba a ajustar los filtros o registra un nuevo seguimiento</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((item) => (
            <div
              key={item.id}
              className="bg-white border border-background-200 rounded-2xl p-5 hover:shadow-soft transition-all duration-200 flex flex-col justify-between"
            >
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${getTipoColor(
                      item.tipoIntervencion
                    )}`}
                  >
                    <i className={getTipoIcon(item.tipoIntervencion)} />
                    {getTipoLabel(item.tipoIntervencion)}
                  </span>
                  <div className="flex gap-1.5">
                    {item.confidencial && (
                      <span className="bg-red-50 text-red-700 border border-red-100 text-[10px] px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                        <i className="ri-lock-line text-xs" /> Privado
                      </span>
                    )}
                    <span className="text-[11px] text-foreground-400 font-mono">
                      {item.fechaFormatted || item.fecha}
                    </span>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-bold text-foreground-400 uppercase tracking-wider">
                    Evolución / Avance
                  </p>
                  <p className="text-sm font-medium text-foreground-800 mt-0.5 line-clamp-3">
                    {item.descripcion}
                  </p>
                </div>

                {item.acuerdos && (
                  <div className="bg-background-50/50 rounded-xl p-3 border border-background-100">
                    <p className="text-[10px] font-bold text-foreground-400 uppercase tracking-wider">
                      Compromisos
                    </p>
                    <p className="text-xs text-foreground-600 mt-1 italic">
                      "{item.acuerdos}"
                    </p>
                  </div>
                )}

                {item.proximosPasos && (
                  <div>
                    <span className="text-[10px] font-bold text-foreground-400 uppercase tracking-wider block">
                      Próxima Fecha / Tarea
                    </span>
                    <p className="text-xs text-foreground-600 mt-0.5">
                      {item.proximosPasos}
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-3 border-t border-background-100 flex items-center justify-between text-[11px]">
                <div className="flex items-center gap-1.5 text-foreground-500">
                  <i className="ri-user-heart-line text-xs" />
                  <span>
                    Profesional: {item.responsableName}
                  </span>
                </div>
                <span className="text-sky-600 bg-sky-50 px-2.5 py-0.5 rounded-full font-semibold border border-sky-100">
                  En Proceso
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}